import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {environment} from '../../../environments/environment';
import {Subscription} from 'rxjs';
import {AuthService} from '../../services/auth/auth.service';
import {Sector} from '../../generated/sales-terminal-client';
import {DetailedSector} from '../../modules/sales-terminal/event-purchase/ticket-select/ticket-select.component';
import {Privileges} from '../../modules/admin/privileges/privileges.constants';
declare const piletilevi: any;

/**
 * Venue map component. Sample usage:
 * <app-venue-map
 *   [confId]="confId"
 *   [sectionsMapType]="sectionsMapType"
 *   [sections]="sections"
 *   [enabledSections]="enabledSections"
 *   [selectedSection]="selectedSection"
 *   [sectionDetails]="sectionDetails"
 *   [zoomLevel]="zoomLevel"
 *   [selectedSeats]="selectedSeats"  <--- use this to update selected seats view, or to clear selected seats. NOTE: this will clear any previously selected seats that are not included in this array
 *
 *   (selected)="addSeatById($event)"
 *   (allSelected)="addSeatsByIds($event)"
 *   (deselected)="removeSeatById($event)"
 *   (sectionSelected)="selectSectionById($event)"
 * ></app-venue-map>
 */

@Component({
  selector: 'app-venue-map',
  templateUrl: './venue-map.component.html',
  styleUrls: ['./venue-map.component.scss']
})
export class VenueMapComponent implements AfterViewInit, OnDestroy {
  @ViewChild('map', { static: true }) public map: ElementRef;
  private _venueMap: any;
  private _windowMode: boolean;
  private _selectedSection: Sector;
  private _resizedExternally: boolean;
  private _hasSectorMap: boolean;
  private _hasPlacesMap: boolean;

  @Input() suggestEnabled: boolean;
  @Input() canRefund: boolean;
  @Input()
  set displayVenueMap(value: boolean) {
    if (value) {
      this.checkIfVenueIsLoaded();
      this.resize();
    }
  };

  @Output() public selected: EventEmitter<number> = new EventEmitter<number>();
  @Output() public allSelected: EventEmitter<number[]> = new EventEmitter<number[]>();
  @Output() public deselected: EventEmitter<number> = new EventEmitter<number>();
  @Output() public sectionSelected: EventEmitter<number> = new EventEmitter<number>();
  @Output() public bookedSeatClicked: EventEmitter<any> = new EventEmitter<number>();
  @Output() public bookedSeatHovered: EventEmitter<any> = new EventEmitter<number>();
  @Output() public openCustomerScreen: EventEmitter<any> = new EventEmitter<any>();
  @Output() public refundSave: EventEmitter<any> = new EventEmitter<any>();
  @Output() public refundClear: EventEmitter<any> = new EventEmitter<any>();
  @Output() public venueMapExists: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public sectionHover: EventEmitter<any> = new EventEmitter<number>();

  private _subscriptions: Subscription[] = [];

  constructor(public authService: AuthService,
              private translateService: TranslateService) {
    this._venueMap = new piletilevi.venuemap.VenueMap();
    this._venueMap.setShopDomain(environment.venueMap.shopDomain);
    this._venueMap.setExtensionHandler(() => {
      this.resize();
    });
    this.checkIfVenueIsLoaded();
  }

  ngAfterViewInit(): void {
    this.configureMap();
    this.addHandlersToMap();
    this.map.nativeElement.appendChild(this._venueMap.getComponentElement());
    this._subscriptions.push(this.translateService.onLangChange.subscribe(() => {
      this.updateTranslations();
      this._venueMap.updateTooltip();
      this._venueMap.update();
    }));
    setTimeout(() => {
      this._venueMap.resize();
    }, 1000);
  }
  ngOnDestroy(): void {
    this._venueMap.dispose();
    this._subscriptions.forEach(s => s.unsubscribe());
    this._subscriptions = [];
  }

  @Input()
  set externalResize(value: boolean) {
    if (this._resizedExternally !== value) {
      this._resizedExternally = value;
      this.resize();
    }
  }

  @Input()
  set confId(value: number) {
    this._venueMap.setConfId(value);
  }

  @Input()
  set sectionsMapType(value: string) {
    if (value) {
      this._venueMap.setSectionsMapType(value);
      this._venueMap.update();
    }
  }

  @Input()
  set sectionDetails(value: DetailedSector) {
    if (value && value.id) {
      this._venueMap.addSectionDetails(value);
      this._venueMap.update();
    }
  }

  @Input()
  set selectedSection(value: Sector) {
    this._selectedSection = value;
    this._venueMap.setSelectedSection(value?.id, value?.hasSeats);
    this._venueMap.update();
    this.resize();
    if (value && (value.id > 0)) {
      this._venueMap.displayMiniSectionsMap();
    } else {
      this._venueMap.hideMiniSectionsMap();
    }
  }
  get selectedSection(): Sector {
    return this._selectedSection;
  }

  @Input()
  set activeSection(value: number) {
    this._venueMap.setActiveSection(value);
  }

  @Input()
  set sections(value: Sector[]) {
    this._venueMap.setSections(value.map(sector => sector.id));
    this._venueMap.update();
  }

  @Input()
  set enabledSections(value: number[]) {
    this._venueMap.setEnabledSections(value);
    this._venueMap.update();
  }

  @Input()
  set zoomLevel(level: number) {
    if (level >= 0) {
      this._venueMap.setZoomLevel(level, false, null);
      this._venueMap.update();
    }
  }

  @Input()
  set selectedSeats(seatIds: number[]) {
    this._venueMap.setSelectedSeats(seatIds);
    this._venueMap.update();
  }

  @Input()
  set selectedSeatsAmount(selectedSeatsAmount: number) {
    this._venueMap.toggleSelectedAmountToolTip(selectedSeatsAmount);
    this._venueMap.update();
    this._venueMap.getSelectedAmountToolTip().updateAmount();
  }

  @Input()
  set deselectedSeats(seatIds: number[]) {
    this._venueMap.unSetSelectedSeats(seatIds);
    this._venueMap.update();
  }

  @Input()
  set currency(value: string) {
    if (value) {
      this._venueMap.setCurrency(value);
      this._venueMap.update();
    }
  }

  @Input()
  set showMiniVenueMap(value: any) {
    if (this._venueMap.getSelectedSection() > 0) {
      this._venueMap.displayMiniSectionsMap();
    }
  }

  @Input()
  set disableCanvasDragging(value: any) {
    this._venueMap.setCanDrag(!value);
  }

  @Input()
  set windowMode(value) {
    this._windowMode = value;
    if (value) {
      this._venueMap.setExtended();
    }
  }
  get windowMode(): boolean {
    return this._windowMode;
  }

  @Input()
  set refundMode(value) {
    if (value !== undefined && this._venueMap.getRefundMode() !== value) {
      this._venueMap.setRefundMode(value);
      this._venueMap.update();
      this._venueMap.displayMiniSectionsMap();
    }
  }
  get refundMode(): boolean {
    return this._venueMap.getRefundMode();
  }

  @Input()
  set clientName(value: string) {
    if (value) {
      this._venueMap.setClientName(value);
      this._venueMap.update();
      this._venueMap.getPlaceToolTip().updateClient();
    }
  }

  @Input()
  set mobileVenueMap(value: boolean) {
    if (value) {
      this._venueMap.setMobileVenueMap(value);
      this._venueMap.update();
    }
  }

  private configureMap(): void {
    this._venueMap.setSeatSelectionEnabled(true);
    this._venueMap.setMassSelectable(true);
    this._venueMap.setWithControls(true);
    this._venueMap.setSectionsMapType('vector');
    this.updateTranslations();
    this._venueMap.update();
  }

  private updateTranslations(): void {
    this._venueMap.addTranslation('section', this.translateService.instant('general.api_venue_map_section'));
    this._venueMap.addTranslation('row', this.translateService.instant('general.api_venue_map_row'));
    this._venueMap.addTranslation('place', this.translateService.instant('general.api_venue_map_place'));
    this._venueMap.addTranslation('price', this.translateService.instant('general.api_venue_map_price'));
    this._venueMap.addTranslation('booked', this.translateService.instant('general.api_venue_map_booked'));
    this._venueMap.addTranslation('available', this.translateService.instant('general.api_venue_map_available'));
    this._venueMap.addTranslation('selected', this.translateService.instant('general.api_venue_map_selected'));
    this._venueMap.addTranslation('not_sold', this.translateService.instant('general.api_venue_map_not_sold'));
    this._venueMap.addTranslation('sold', this.translateService.instant('general.api_venue_map_sold'));
    this._venueMap.addTranslation('stage-stage', this.translateService.instant('general.api_venue_map_stage'));
    this._venueMap.addTranslation('unavailable', this.translateService.instant('general.api_venue_map_unavailable'));
    this._venueMap.addTranslation('client', this.translateService.instant('general.api_venue_map_client'));
    this._venueMap.addTranslation('selected_seats', this.translateService.instant('general.api_venue_map_selected_seats'));
  }

  private checkIfVenueIsLoaded(): void {
    if (this._hasPlacesMap || this._hasSectorMap) {
      this.venueMapExists.emit(true);
    } else {
      this.venueMapExists.emit(false);
    }
  }

  private addHandlersToMap(): void {
    this._venueMap.addHandler('seatSelected', (seatId: number) => {
      this.selected.emit(seatId);
      this.showMini();
    });
    this._venueMap.addHandler('seatsSelected', (seatIds: number[]) => this.allSelected.emit(seatIds));
    this._venueMap.addHandler('seatUnSelected', (seatId: number) => {
      this.deselected.emit(seatId);
      this.showMini();
    });
    this._venueMap.addHandler('sectionSelected', (sectionId: number) => this.sectionSelected.emit(sectionId));
    this._venueMap.addHandler('sectionsMapLoadSuccess', () => {
      this._hasSectorMap = true;
      this.checkIfVenueIsLoaded();
    });
    this._venueMap.addHandler('sectionsMapLoadFailure', () => {
      this._hasSectorMap = false;
      this.checkIfVenueIsLoaded();
    });
    this._venueMap.addHandler('sectionMouseover', (sectionId: number) => {
      this.sectionHover.emit(sectionId);
    });
    this._venueMap.addHandler('sectionMouseout', (sectionId: number) => {
      this.sectionHover.emit(null);
      if (this._venueMap.getSelectedSection() === sectionId) {
        this._venueMap.displayMiniSectionsMap();
      }
    });
    this._venueMap.addHandler('bookedSeatClicked', (bookingId: number) => this.bookedSeatClicked.emit(bookingId));
    this._venueMap.addHandler('bookedSeatHovered', (bookingId: number) => this.bookedSeatHovered.emit(bookingId));
    this._venueMap.addHandler('venueLoaded', (venueFound: boolean) => {
      this.venueMapExists.emit(venueFound);
    });
    this._venueMap.addHandler('placesMapLoadSuccess', () => {
      this._hasPlacesMap = true;
      this.checkIfVenueIsLoaded();
    });
    this._venueMap.addHandler('placesMapLoadFailure', () => {
      this._hasPlacesMap = false;
      this.checkIfVenueIsLoaded();
    });
  }

  private resize(): void {
    setTimeout(() => {
      this._venueMap.setCurrentZoomLevel(0);
      this._venueMap.resize();
    }, 100);
  }

  private showMini(): void {
    setTimeout(() => {
      this._venueMap.displayMiniSectionsMap();
    }, 100);
  }

  openExternalWindow(): void {
    this.openCustomerScreen.emit();
  }
  refundModeSave(): void {
    this.refundSave.emit();
  }
  refundModeClear(): void {
    this.refundClear.emit();
  }

  get isExpanded(): boolean {
    return this._venueMap.isExtended();
  }

  public get isRefundMode(): boolean {
    return !!(this.canRefund && this.authService.checkPrivileges(Privileges.VENUEMAP_REFUND) && this.selectedSection && !this.suggestEnabled && this.refundMode);
  }
}
